/* Stage 5 — Phase B8: Workstage 5 (Project Transition) admin UI.

   Five full-width vertical modules per §6 of the brief and the
   accepted canonical workspace shell.  All actions are thin wrappers
   over the B8 backend endpoints on `/api/admin/projects/{pid}/ws5/*`.

   Reference: 551-01 (main WS5 workspace), 551-02 (Package Builder),
   551-03 (Appointment / Signature).  Illustrative colours/data from
   the prototypes are ignored per §3.

   Final Closeout Correction extends this file with:
   - §1 authorised 551-02 editable Package Builder (per-package
     configuration + preview of generated operational content).
   - §2 authorised 551-03 B8-scoped Appointment Management surface
     (Appointment Summary / Signatories / Execution & Document /
     Package Activation Conditions / Current Appointed Scope).
   Addenda / Variations remain deferred (§34 lock).
*/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import RecordExternalExecutionModal, {
    EXT_METHOD_LABELS,
    uuid4 as _uuid4,
} from "./RecordExternalExecutionModal";
import UploadSignedAddendumModal from "./UploadSignedAddendumModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase 3 — simple reason/note prompt modal used by the three secondary
// appointment action buttons (partial, withdraw, flag).  Deliberately
// thin — one text input, no file picker, no signer list.
function _AppointmentActionPrompt({ open, title, label, required, submitLabel,
                                     busy, err, value, onChange, onCancel,
                                     onSubmit, testIdPrefix }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-6"
             data-testid={`${testIdPrefix}-modal`}>
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 my-20">
                <h3 className="font-manrope text-[14px] font-semibold mb-3">{title}</h3>
                <label className="block text-[12px] font-medium mb-1">
                    {label}{required ? " *" : ""}
                </label>
                <textarea rows={3} value={value}
                          onChange={e => onChange(e.target.value)}
                          data-testid={`${testIdPrefix}-text`}
                          className="w-full border rounded px-2 py-1 text-[12px] mb-3" />
                {err && (
                    <div data-testid={`${testIdPrefix}-error`}
                         className="mb-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded p-2">
                        {err}
                    </div>
                )}
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={onCancel} disabled={busy}
                            data-testid={`${testIdPrefix}-cancel`}
                            className="text-[12px] px-3 py-1.5">Cancel</button>
                    <button type="button" onClick={onSubmit} disabled={busy}
                            data-testid={`${testIdPrefix}-submit`}
                            className="text-[12px] px-3 py-1.5 rounded bg-[#A8543F] text-white">
                        {busy ? "Submitting…" : submitLabel}
                    </button>
                </div>
            </div>
        </div>);
}

function Pill({ tone = "neutral", children, testid }) {
    const map = {
        neutral: "bg-[#F5F3EE] text-[#1D1D1B]",
        good: "bg-[#E4EFE4] text-[#345E34]",
        warn: "bg-[#FBEEDF] text-[#7A4A22]",
        active: "bg-[#A8543F] text-white",
    };
    return (
        <span data-testid={testid}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${map[tone]}`}>
            {children}
        </span>
    );
}

function ModuleCard({ n, title, testid, children }) {
    return (
        <section data-testid={testid}
                    className="border border-[#E5E2DC] rounded-lg bg-white p-4 shadow-sm">
            <header className="mb-3 flex items-baseline gap-3">
                <span className="font-manrope text-[11px] uppercase tracking-widest text-[#A8543F]">
                    Module {n}
                </span>
                <h2 className="font-manrope text-[15px] font-semibold text-[#1D1D1B]">
                    {title}
                </h2>
            </header>
            <div className="text-[13px] text-[#1D1D1B]">{children}</div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div className="grid grid-cols-2 gap-2 py-1 text-[13px]">
            <div className="text-[#666]">{label}</div>
            <div>{children}</div>
        </div>
    );
}

function stringifyForEdit(v) {
    if (v == null) return "";
    if (typeof v === "string") return v;
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}


export function WS5ActiveModules({ projectId, mode = "active", onCompleted }) {
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");
    const [builderPkg, setBuilderPkg] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const r = await axios.get(
                `${API}/admin/projects/${projectId}/ws5`,
                { withCredentials: true });
            setData(r.data);
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        }
    }, [projectId]);

    useEffect(() => { refresh(); }, [refresh]);

    if (err) return (
        <div data-testid="ws5-error" className="text-[13px] text-red-700">
            {err}
        </div>);
    if (!data) return (
        <div data-testid="ws5-loading"
                className="text-[13px] text-[#666]">Loading WS5…</div>);

    const rev = data.transition_review;
    const fdp = rev.final_design_position;
    const ccc = rev.client_commercial_context;
    const packages = data.packages || [];
    const proposals = data.proposals || [];
    const appointments = data.appointments || [];
    const compl = data.completion_readiness || {};

    // D5 Slice C — coherent mode propagation via fieldset gate.
    const readOnly = mode !== "active";
    return (
        <>
        <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0"
                  data-testid={`ws5-fieldset-mode-${mode}`}>
        {readOnly && (
            <div className="mb-3 px-3 py-2 rounded-md bg-[#F5F1EA] border border-[#E5E2DC] text-[12px] text-[#666]"
                 data-testid={`ws5-mode-banner-${mode}`}>
                {mode === "future"
                    ? "This Workstage is not yet active. Content is shown for reference only."
                    : "This Workstage is completed. Historical data shown, read-only."}
            </div>
        )}
        <div className="flex flex-col gap-4" data-testid="ws5-active-modules">
            {/* Module 1 — Transition Review */}
            <ModuleCard n={1} title="Transition Review"
                            testid="ws5-module-1-transition-review">
                <div className="mb-3">
                    <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-1">
                        Free Design Position
                    </h4>
                    <Field label="WS4 status">
                        <Pill tone={fdp.ws4_completed ? "good" : "warn"}
                                testid="ws5-fdp-status">
                            {fdp.ws4_completed ? "completed" : "not completed"}
                        </Pill>
                    </Field>
                    <Field label="Final Design Issue">
                        <span data-testid="ws5-final-di">
                            {fdp.final_design_issue_title || "—"}
                            {fdp.final_issue_date
                                ? ` · ${fdp.final_issue_date}` : ""}
                        </span>
                    </Field>
                    <Field label="Package Summary">
                        <Pill tone={fdp.package_summary_state === "complete"
                            ? "good" : "warn"}
                            testid="ws5-summary-state">
                            {fdp.package_summary_state}
                        </Pill>
                    </Field>
                    <Field label="Final / Current deliverables">
                        <span data-testid="ws5-final-deliverables">
                            {fdp.final_current_deliverables.length === 0
                                ? "None"
                                : fdp.final_current_deliverables
                                    .map(d => d.name).join(", ")}
                        </span>
                    </Field>
                    <Field label="Feedback">
                        <span data-testid="ws5-feedback-position">
                            {fdp.feedback.resolved} resolved · {fdp.feedback.open} open
                        </span>
                    </Field>
                    {fdp.items_deferred && (
                        <Field label="Items deferred">
                            <span data-testid="ws5-items-deferred">
                                {fdp.items_deferred}
                            </span>
                        </Field>
                    )}
                </div>
                <div>
                    <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-1">
                        Client / Commercial Context
                    </h4>
                    <Field label="Future service intention">
                        <span data-testid="ws5-future-intention">
                            {ccc.future_service_intention || "—"}
                        </span>
                    </Field>
                    <Field label="Key client decisions">
                        <span data-testid="ws5-key-decisions">
                            {ccc.key_client_decisions || "—"}
                        </span>
                    </Field>
                </div>
            </ModuleCard>

            {/* Module 2 — Service Package Selection */}
            <ModuleCard n={2} title="Service Package Selection"
                            testid="ws5-module-2-package-selection">
                {packages.length === 0
                    ? <div data-testid="ws5-no-packages"
                              className="text-[#666]">No packages selected yet.</div>
                    : (
                        <table className="w-full text-[13px]"
                                data-testid="ws5-packages-table">
                            <thead className="text-left text-[11px] uppercase text-[#666]">
                                <tr>
                                    <th className="py-1">Package</th>
                                    <th>Kind</th>
                                    <th>Status</th>
                                    <th>Template bound</th>
                                    <th>Activation ready</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {packages.map(p => (
                                    <tr key={p.id}
                                        className="border-t border-[#F0EDE7]"
                                        data-testid={`ws5-pkg-row-${p.id}`}>
                                        <td className="py-1.5">{p.package_name}</td>
                                        <td className="text-[#666]">
                                            {p.package_kind || "custom"}
                                        </td>
                                        <td>
                                            <Pill
                                                tone={p.status === "Active" ? "good"
                                                    : p.status === "Appointed" ? "active"
                                                    : "neutral"}
                                                testid={`ws5-pkg-status-${p.id}`}>
                                                {p.status}
                                            </Pill>
                                        </td>
                                        <td className="text-[#666] text-[12px]">
                                            {p.template_version_id
                                                ? `${p.package_code} v${p.template_version}`
                                                : "—"}
                                        </td>
                                        <td>
                                            <Pill
                                                tone={p.activation_readiness?.ready
                                                    ? "good" : "warn"}
                                                testid={`ws5-pkg-ready-${p.id}`}>
                                                {p.activation_readiness?.ready
                                                    ? "ready" : "blocked"}
                                            </Pill>
                                        </td>
                                        <td className="text-right">
                                            <button type="button"
                                                data-testid={`ws5-pkg-open-builder-${p.id}`}
                                                onClick={() => setBuilderPkg(p)}
                                                className="text-[12px] text-[#A8543F] underline">
                                                Open Builder
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
            </ModuleCard>

            {/* Module 3 — Proposal / Commercial Offer */}
            <ModuleCard n={3} title="Proposal / Commercial Offer"
                            testid="ws5-module-3-proposal">
                {proposals.length === 0
                    ? <div data-testid="ws5-no-proposals"
                              className="text-[#666]">No proposals yet.</div>
                    : (
                        <ul className="text-[13px]"
                             data-testid="ws5-proposals-list">
                            {proposals.map(p => (
                                <li key={p.id}
                                    className="py-1 flex items-center gap-2"
                                    data-testid={`ws5-prop-row-${p.id}`}>
                                    <span>{p.title} · rev {p.revision}</span>
                                    <Pill tone={p.status === "Accepted" ? "good"
                                        : p.status === "Issued" ? "active" : "neutral"}
                                        testid={`ws5-prop-status-${p.id}`}>
                                        {p.status}
                                    </Pill>
                                </li>
                            ))}
                        </ul>
                    )}
            </ModuleCard>

            {/* Module 4 — Appointment Management (551-03 B8-scoped) */}
            <ModuleCard n={4} title="Appointment Management"
                            testid="ws5-module-4-appointment">
                {appointments.length === 0
                    ? <div data-testid="ws5-no-appointments"
                              className="text-[#666]">
                        No appointment created yet.  Create from an
                        accepted proposal to begin the appointment /
                        signature workflow.
                    </div>
                    : (
                        <div className="flex flex-col gap-6"
                              data-testid="ws5-appointments-list">
                            {appointments.map(a => (
                                <AppointmentPanel key={a.id}
                                    projectId={projectId}
                                    apt={a}
                                    proposals={proposals}
                                    packages={packages}
                                    onChanged={refresh} />
                            ))}
                        </div>
                    )}
            </ModuleCard>

            {/* Module 5 — Transition Decision / Completion */}
            <ModuleCard n={5} title="Transition Decision / Completion"
                            testid="ws5-module-5-completion">
                <div className="mb-2 flex items-center gap-2"
                        data-testid="ws5-completion-panel">
                    <strong>Ready to complete:</strong>
                    <Pill tone={compl.ready ? "good" : "warn"}
                             testid="ws5-completion-ready">
                        {compl.ready ? "yes" : "no"}
                    </Pill>
                </div>
                {compl.ready ? (
                    <div className="text-[13px] text-[#345E34]"
                            data-testid="ws5-can-complete">
                        WS5 → WS6 transition available.
                    </div>
                ) : (
                    <ul className="list-disc pl-5 text-[13px] text-[#7A5148]"
                            data-testid="ws5-blockers-list">
                        {(compl.reasons || []).map((r, i) => (
                            <li key={i} data-testid={`ws5-blocker-${i}`}>
                                {r}
                            </li>
                        ))}
                    </ul>
                )}
                <div className="mt-3 text-[12px] text-[#666]">
                    Place on Hold and Close Project remain in the
                    global Project State workflow (not shown here).
                </div>
            </ModuleCard>
        </div>
        </fieldset>
        {builderPkg && (
            <PackageBuilderModal
                projectId={projectId}
                pkg={builderPkg}
                onClose={() => setBuilderPkg(null)}
                onDone={() => { setBuilderPkg(null); refresh(); }} />
        )}
        </>
    );
}


// ---------------------------------------------------------------------------
// 551-02 — Package Configuration / Proposal Builder (editable).
// ---------------------------------------------------------------------------

function PackageBuilderModal({ projectId, pkg, onClose, onDone }) {
    const snap = pkg.snapshot || {};
    const cfg = pkg.project_configuration || {};

    // Editable fields — each authorised by §1 and mapped to the A9
    // `project_configuration` shape.  Deliverables / prerequisites /
    // activation conditions accept JSON so complex list edits remain
    // possible without a bespoke list editor in this cycle.
    const [scope, setScope] = useState(
        cfg.project_specific_scope ?? "");
    const [exclusions, setExclusions] = useState(
        cfg.exclusions ?? "");
    const [deliverables, setDeliverables] = useState(
        stringifyForEdit(cfg.deliverables ?? snap.template_deliverables ?? []));
    const [pricingMethod, setPricingMethod] = useState(
        cfg.pricing_method || snap.pricing_method || "fixed_fee");
    const [feeAmount, setFeeAmount] = useState(
        (cfg.fee_amount ?? snap.fixed_fee?.amount ?? "").toString());
    const [feeCurrency, setFeeCurrency] = useState(
        cfg.fee_currency || snap.fixed_fee?.currency || "GBP");
    const [paymentTerms, setPaymentTerms] = useState(
        stringifyForEdit(cfg.payment_terms ?? snap.payment_terms ?? {}));
    const [prerequisites, setPrerequisites] = useState(
        stringifyForEdit(cfg.prerequisites ?? snap.prerequisites ?? []));
    const [activationConditions, setActivationConditions] = useState(
        stringifyForEdit(cfg.activation_conditions
            ?? snap.activation_conditions ?? []));
    const [packageTerms, setPackageTerms] = useState(
        stringifyForEdit(cfg.package_specific_terms
            ?? snap.package_terms ?? {}));
    const [clientNotes, setClientNotes] = useState(
        cfg.client_facing_notes ?? "");
    const [saving, setSaving] = useState(false);
    const [saveErr, setSaveErr] = useState("");
    const [saveInfo, setSaveInfo] = useState("");

    const previewCount = useMemo(() => (
        (snap.template_deliverables?.length || 0)
        + (snap.template_internal_tasks?.length || 0)
        + (snap.template_client_actions?.length || 0)
        + (snap.template_programme_items?.length || 0)
    ), [snap]);

    const parseJSON = (raw, fallback) => {
        const s = (raw || "").trim();
        if (!s) return fallback;
        try { return JSON.parse(s); } catch { throw new Error("invalid JSON"); }
    };

    const onSave = async () => {
        setSaving(true); setSaveErr(""); setSaveInfo("");
        try {
            const updates = {
                project_configuration: {
                    project_specific_scope: scope,
                    exclusions,
                    deliverables: parseJSON(deliverables, []),
                    pricing_method: pricingMethod,
                    fee_amount: feeAmount === "" ? null
                        : Number.parseFloat(feeAmount),
                    fee_currency: feeCurrency,
                    payment_terms: parseJSON(paymentTerms, {}),
                    prerequisites: parseJSON(prerequisites, []),
                    activation_conditions:
                        parseJSON(activationConditions, []),
                    package_specific_terms: parseJSON(packageTerms, {}),
                    client_facing_notes: clientNotes,
                },
            };
            await axios.patch(
                `${API}/admin/projects/${projectId}/ws5/packages/${pkg.id}`,
                {
                    expected_version: pkg.record_version,
                    edit_op_id: `ws5-builder-${pkg.id}-${Date.now()}`,
                    updates,
                },
                { withCredentials: true });
            setSaveInfo("Package configuration saved.");
            // Give the user a beat to see confirmation, then close.
            setTimeout(() => { onDone && onDone(); }, 250);
        } catch (e) {
            setSaveErr(e?.response?.data?.reason
                       || e?.response?.data?.error
                       || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="ws5-package-builder-modal"
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
            <div className="bg-white rounded-lg shadow-lg border border-[#E5E2DC] w-full max-w-3xl max-h-[92vh] overflow-y-auto">
                <header className="border-b border-[#E5E2DC] px-4 py-3 flex items-center">
                    <h3 className="font-manrope text-[13px] font-semibold uppercase tracking-wide"
                        data-testid="ws5-builder-title">
                        Package Builder — {pkg.package_name}
                    </h3>
                    <button type="button" onClick={onClose}
                        data-testid="ws5-package-builder-close"
                        className="ml-auto text-[13px] text-[#666]">
                        Close
                    </button>
                </header>
                <div className="px-4 py-4 text-[13px]">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                        <Field label="Template bound">
                            <span data-testid="ws5-builder-template-binding">
                                {pkg.template_version_id
                                    ? `${pkg.package_code} v${pkg.template_version} (${pkg.template_catalogue_state_at_bind || "Active"})`
                                    : "Project-specific custom package"}
                            </span>
                        </Field>
                        <Field label="Status">
                            <span data-testid="ws5-builder-status">{pkg.status}</span>
                        </Field>
                    </div>

                    <BuilderField id="scope" label="Project-Specific Scope"
                        testid="ws5-builder-scope"
                        value={scope} onChange={setScope} rows={3} />
                    <BuilderField id="exclusions" label="Exclusions"
                        testid="ws5-builder-exclusions"
                        value={exclusions} onChange={setExclusions} rows={2} />
                    <BuilderField id="deliverables" label="Deliverables (JSON)"
                        testid="ws5-builder-deliverables"
                        value={deliverables} onChange={setDeliverables} rows={4} />

                    <div className="grid grid-cols-3 gap-3">
                        <label className="block">
                            <span className="text-[#666] text-[12px]">Pricing method</span>
                            <select
                                data-testid="ws5-builder-pricing-method"
                                value={pricingMethod}
                                onChange={(e) => setPricingMethod(e.target.value)}
                                className="mt-1 block w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px]">
                                <option value="fixed_fee">fixed_fee</option>
                                <option value="hourly">hourly</option>
                                <option value="percentage">percentage</option>
                                <option value="tbc">tbc</option>
                            </select>
                        </label>
                        <label className="block">
                            <span className="text-[#666] text-[12px]">Fee amount</span>
                            <input type="number" step="0.01"
                                data-testid="ws5-builder-fee-amount"
                                value={feeAmount}
                                onChange={(e) => setFeeAmount(e.target.value)}
                                className="mt-1 block w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px]" />
                        </label>
                        <label className="block">
                            <span className="text-[#666] text-[12px]">Currency</span>
                            <input type="text"
                                data-testid="ws5-builder-fee-currency"
                                value={feeCurrency}
                                onChange={(e) => setFeeCurrency(e.target.value)}
                                className="mt-1 block w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px]" />
                        </label>
                    </div>

                    <BuilderField id="payment-terms" label="Payment Terms (JSON)"
                        testid="ws5-builder-payment-terms"
                        value={paymentTerms} onChange={setPaymentTerms} rows={3} />
                    <BuilderField id="prerequisites"
                        label="Prerequisites / Dependencies (JSON)"
                        testid="ws5-builder-prerequisites"
                        value={prerequisites} onChange={setPrerequisites}
                        rows={3} />
                    <BuilderField id="activation-conditions"
                        label="Activation Conditions (JSON)"
                        testid="ws5-builder-activation-conditions"
                        value={activationConditions}
                        onChange={setActivationConditions} rows={3} />
                    <BuilderField id="package-terms"
                        label="Package-Specific Terms (JSON)"
                        testid="ws5-builder-package-terms"
                        value={packageTerms} onChange={setPackageTerms}
                        rows={3} />
                    <BuilderField id="client-notes"
                        label="Client-Facing Summary / Notes"
                        testid="ws5-builder-client-notes"
                        value={clientNotes} onChange={setClientNotes} rows={2} />

                    <div className="mt-4 border-t border-[#E5E2DC] pt-3">
                        <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-1">
                            Generated content preview
                        </h4>
                        <div data-testid="ws5-builder-preview-count"
                                className="text-[12px] text-[#666]">
                            {previewCount} template item(s) available for
                            activation. Preview only — NOT instantiated
                            until the package is activated (§11).
                        </div>
                        <ul className="mt-2 text-[12px] text-[#666] list-disc pl-5">
                            <li data-testid="ws5-builder-preview-deliverables">
                                Deliverables: {snap.template_deliverables?.length || 0}
                            </li>
                            <li data-testid="ws5-builder-preview-internal-todos">
                                Internal Team To-Dos: {snap.template_internal_tasks?.length || 0}
                            </li>
                            <li data-testid="ws5-builder-preview-client-actions">
                                Client Actions: {snap.template_client_actions?.length || 0}
                            </li>
                            <li data-testid="ws5-builder-preview-programme">
                                Programme / Mini Programme items: {snap.template_programme_items?.length || 0}
                            </li>
                        </ul>
                    </div>

                    {saveErr && (
                        <div className="mt-3 text-[12px] text-red-700"
                              data-testid="ws5-builder-save-error">
                            {saveErr}
                        </div>)}
                    {saveInfo && (
                        <div className="mt-3 text-[12px] text-[#345E34]"
                              data-testid="ws5-builder-save-info">
                            {saveInfo}
                        </div>)}
                </div>
                <footer className="border-t border-[#E5E2DC] px-4 py-3 flex items-center gap-2">
                    <button type="button"
                        data-testid="ws5-builder-cancel"
                        onClick={onClose}
                        className="text-[13px] text-[#666]">
                        Cancel
                    </button>
                    <button type="button"
                        data-testid="ws5-builder-save"
                        disabled={saving}
                        onClick={onSave}
                        className="ml-auto rounded bg-[#A8543F] text-white text-[13px] px-3 py-1.5 disabled:opacity-50">
                        {saving ? "Saving…" : "Save configuration"}
                    </button>
                </footer>
            </div>
        </div>
    );
}


function BuilderField({ id, label, value, onChange, rows = 2, testid }) {
    return (
        <label className="block mb-2">
            <span className="text-[#666] text-[12px]">{label}</span>
            <textarea
                id={`builder-${id}`}
                data-testid={testid}
                rows={rows}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 block w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px] font-mono" />
        </label>
    );
}


// ---------------------------------------------------------------------------
// 551-03 — Appointment Management panels (B8-scoped, no Addenda).
// ---------------------------------------------------------------------------

function AppointmentPanel({ projectId, apt, proposals, packages, onChanged }) {
    const parties = apt.parties || [];
    const includedIds = apt.included_service_package_ids || [];
    const includedPkgs = packages.filter(
        (p) => includedIds.includes(p.id));
    const sourceProp = proposals.find(
        (p) => p.id === apt.source_proposal_revision_id);

    const [signaturesRow, setSignaturesRow] = useState(null);
    // Communications Safety Correction — confirm modal for WS5 package
    // activation so admin can opt in / out of the client email.
    const [confirmActivate, setConfirmActivate] = useState(null);
    const [confirmNotify, setConfirmNotify] = useState(false);
    const [confirmBusy, setConfirmBusy] = useState(false);
    const doActivate = async () => {
        if (!confirmActivate) return;
        setConfirmBusy(true);
        const p = confirmActivate;
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws5/packages/${p.id}/activate`,
                { activation_op_id: `activate-${p.id}-${Date.now()}`,
                  expected_version: p.record_version,
                  notify_client: confirmNotify },
                { withCredentials: true });
            setConfirmActivate(null);
            setConfirmNotify(false);
            onChanged && onChanged();
        } catch (e) {
            alert(e?.response?.data?.reason || e.message);
        } finally { setConfirmBusy(false); }
    };

    // Phase 3 — appointment lifecycle actions (upload-execute + 3 wrappers).
    const [showExecUpload, setShowExecUpload] = useState(false);
    const [showUploadAddendum, setShowUploadAddendum] = useState(false);
    const [actionKind, setActionKind] = useState(null);  // "partial" | "withdraw" | "flag"
    const [actionText, setActionText] = useState("");
    const [actionBusy, setActionBusy] = useState(false);
    const [actionErr, setActionErr] = useState("");
    const [toast, setToast] = useState("");

    const extSigners = apt.external_signers || [];
    const executedDocs = apt.executed_documents || [];
    const execEv = apt.execution_evidence || {};
    const basisIsLrs = (apt.contract_basis
                        || "lrs_paid_services_appointment")
                        === "lrs_paid_services_appointment";
    const showExecUploadBtn = basisIsLrs
        && ["Draft", "Sent for Signature", "Partially Signed"].includes(apt.status);
    const showAddendumBtns = basisIsLrs && apt.status === "Executed";
    const showPartialBtn = apt.status === "Sent for Signature";
    const showWithdrawBtn = !["Executed", "Withdrawn"].includes(apt.status);
    const showFlagBtn = !["Executed", "Withdrawn", "Terminated"].includes(apt.status);

    const openAction = (kind) => {
        setActionKind(kind);
        setActionText("");
        setActionErr("");
    };
    const closeAction = () => {
        if (actionBusy) return;
        setActionKind(null); setActionText(""); setActionErr("");
    };
    const submitAction = async () => {
        setActionErr("");
        const text = (actionText || "").trim();
        if (actionKind !== "partial" && !text) {
            setActionErr("A description is required for this action.");
            return;
        }
        const paths = {
            partial: "mark-partially-signed",
            withdraw: "withdraw",
            flag: "flag-signature-issue",
        };
        const body = { op_id: _uuid4() };
        if (actionKind === "partial") body.note = text || null;
        if (actionKind === "withdraw") body.reason = text;
        if (actionKind === "flag") body.issue_description = text;
        setActionBusy(true);
        try {
            await axios.post(
                `${API}/admin/projects/${projectId}/ws5/appointments/${apt.id}/${paths[actionKind]}`,
                body, { withCredentials: true });
            setActionKind(null); setActionText("");
            setToast({
                partial: "Appointment marked partially signed.",
                withdraw: "Appointment withdrawn.",
                flag: "Signature issue flagged.",
            }[actionKind]);
            onChanged && onChanged();
        } catch (e) {
            const d = e?.response?.data?.detail || e?.response?.data;
            setActionErr((d && (d.reason || d.message || d.error)) || e.message);
        } finally {
            setActionBusy(false);
        }
    };

    return (
        <div data-testid={`ws5-apt-panel-${apt.id}`}
              className="border border-[#E5E2DC] rounded-md bg-[#FCFBF9] p-3 flex flex-col gap-4">

            {/* Panel 1 — Appointment Summary */}
            <section data-testid={`ws5-apt-summary-${apt.id}`}>
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Appointment Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-6">
                    <Field label="Reference / Title">
                        <span data-testid={`ws5-apt-title-${apt.id}`}>
                            {apt.title || "Appointment"}
                        </span>
                    </Field>
                    <Field label="Source Proposal revision">
                        <span data-testid={`ws5-apt-source-prop-${apt.id}`}>
                            {sourceProp
                                ? `${sourceProp.title} · rev ${sourceProp.revision}`
                                : (apt.source_proposal_revision_id || "—")}
                        </span>
                    </Field>
                    <Field label="Appointment / Document version">
                        <span data-testid={`ws5-apt-version-${apt.id}`}>
                            v{apt.record_version ?? 0}
                        </span>
                    </Field>
                    <Field label="Contract Basis">
                        <span data-testid={`ws5-apt-basis-${apt.id}`}>
                            {apt.contract_basis
                             || "lrs_paid_services_appointment"}
                        </span>
                    </Field>
                    <Field label="Status">
                        <Pill
                            tone={apt.status === "Executed" ? "good"
                                : apt.status === "Sent for Signature"
                                    || apt.status === "Partially Signed"
                                ? "active" : "neutral"}
                            testid={`ws5-apt-state-${apt.id}`}>
                            {apt.status || apt.state || "Draft"}
                        </Pill>
                    </Field>
                    <Field label="Included packages">
                        <span data-testid={`ws5-apt-included-${apt.id}`}>
                            {includedPkgs.length
                                ? includedPkgs.map((p) => p.package_name).join(", ")
                                : (includedIds.length ? `${includedIds.length} package(s)` : "None")}
                        </span>
                    </Field>
                    <Field label="Appointed value">
                        <span data-testid={`ws5-apt-value-${apt.id}`}>
                            {apt.appointed_value != null
                                ? `${apt.appointed_currency || "GBP"} ${apt.appointed_value}`
                                : "—"}
                        </span>
                    </Field>
                    <Field label="Execution / Effective">
                        <span data-testid={`ws5-apt-executed-${apt.id}`}>
                            {apt.executed_at
                                ? `Executed ${String(apt.executed_at).slice(0, 10)}`
                                : "Not executed"}
                        </span>
                    </Field>
                </div>
            </section>

            {/* Panel 2 — Signatories */}
            <section data-testid={`ws5-apt-signatories-${apt.id}`}>
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Signatories
                </h4>
                {parties.length === 0
                    ? <div className="text-[#666] text-[12px]"
                              data-testid={`ws5-apt-no-signatories-${apt.id}`}>
                        No signatories recorded yet.
                    </div>
                    : (
                        <table className="w-full text-[12px]"
                                data-testid={`ws5-apt-signatories-table-${apt.id}`}>
                            <thead className="text-left text-[11px] uppercase text-[#666]">
                                <tr>
                                    <th className="py-1">Signer</th>
                                    <th>Role</th>
                                    <th>Invitation</th>
                                    <th>Signing</th>
                                    <th>Signed at</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {parties.map((s, i) => {
                                    const key = s.signer_id || s.email || `s-${i}`;
                                    const sent = s.invited_at || s.sent_at;
                                    const signed = s.signed_at;
                                    return (
                                        <tr key={key}
                                            className="border-t border-[#F0EDE7]"
                                            data-testid={`ws5-apt-signer-${apt.id}-${i}`}>
                                            <td className="py-1.5">
                                                <span data-testid={`ws5-apt-signer-name-${apt.id}-${i}`}>
                                                    {s.name || s.email || "—"}
                                                </span>
                                            </td>
                                            <td className="text-[#666]">
                                                {s.role || "—"}
                                            </td>
                                            <td>
                                                <Pill tone={sent ? "good" : "neutral"}
                                                    testid={`ws5-apt-signer-invite-${apt.id}-${i}`}>
                                                    {sent ? "sent" : "not sent"}
                                                </Pill>
                                            </td>
                                            <td>
                                                <Pill tone={signed ? "good"
                                                        : sent ? "warn" : "neutral"}
                                                    testid={`ws5-apt-signer-sign-${apt.id}-${i}`}>
                                                    {signed ? "signed"
                                                        : sent ? "pending" : "—"}
                                                </Pill>
                                            </td>
                                            <td className="text-[#666] text-[12px]"
                                                data-testid={`ws5-apt-signer-signed-at-${apt.id}-${i}`}>
                                                {signed
                                                    ? String(signed).slice(0, 16)
                                                    : "—"}
                                            </td>
                                            <td className="text-right">
                                                {signed && (
                                                    <button type="button"
                                                        onClick={() => setSignaturesRow(s)}
                                                        data-testid={`ws5-apt-view-signature-${apt.id}-${i}`}
                                                        className="text-[12px] text-[#A8543F] underline">
                                                        View Signature
                                                    </button>)}
                                                {!signed && sent && apt.status !== "Executed" && (
                                                    <button type="button"
                                                        onClick={() => alert("Resend Invitation is handled by the B6 signer flow.")}
                                                        data-testid={`ws5-apt-resend-invite-${apt.id}-${i}`}
                                                        className="text-[12px] text-[#A8543F] underline ml-2">
                                                        Resend Invitation
                                                    </button>)}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                {signaturesRow && (
                    <div className="mt-2 text-[12px] text-[#666] bg-white border border-[#E5E2DC] rounded p-2"
                          data-testid={`ws5-apt-signature-record-${apt.id}`}>
                        <strong>Signature Record:</strong> {signaturesRow.name || signaturesRow.email}
                        {signaturesRow.signed_at
                            ? ` · signed ${String(signaturesRow.signed_at).slice(0, 16)}`
                            : ""}
                        <button type="button" onClick={() => setSignaturesRow(null)}
                            data-testid={`ws5-apt-signature-record-close-${apt.id}`}
                            className="ml-3 text-[#A8543F] underline">
                            Close
                        </button>
                    </div>)}
            </section>

            {/* Panel 3 — Execution / Document */}
            <section data-testid={`ws5-apt-execution-${apt.id}`}>
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Execution &amp; Document
                </h4>
                <div className="grid grid-cols-2 gap-x-6">
                    <Field label="Required signatures">
                        <span data-testid={`ws5-apt-req-sigs-${apt.id}`}>
                            {parties.length || "—"}
                        </span>
                    </Field>
                    <Field label="Signatures complete">
                        <span data-testid={`ws5-apt-sigs-complete-${apt.id}`}>
                            {parties.filter((s) => s.signed_at).length}/{parties.length || 0}
                        </span>
                    </Field>
                    <Field label="Studio G4 countersign / execution">
                        <Pill
                            tone={apt.status === "Executed" ? "good" : "warn"}
                            testid={`ws5-apt-countersign-${apt.id}`}>
                            {apt.status === "Executed" ? "complete" : "outstanding"}
                        </Pill>
                    </Field>
                    <Field label="Current signing document">
                        <span data-testid={`ws5-apt-signing-doc-${apt.id}`}>
                            {(apt.draft_file_refs || []).length > 0
                                ? `File ${apt.draft_file_refs[0].file_id?.slice(0, 8)}… v${apt.draft_file_refs[0].version}`
                                : "—"}
                        </span>
                    </Field>
                    <Field label="Executed immutable document">
                        <span data-testid={`ws5-apt-executed-doc-${apt.id}`}>
                            {executedDocs.length > 0
                                ? (executedDocs.length === 1
                                    ? `File ${executedDocs[0].file_id?.slice(0, 8)}… v${executedDocs[0].current_version_number}`
                                    : `${executedDocs.length} files attached`)
                                : "—"}
                        </span>
                    </Field>
                    <Field label="Signature audit">
                        <span data-testid={`ws5-apt-audit-${apt.id}`}>
                            {apt.execution_evidence
                                ? "Available via Files foundation"
                                : "—"}
                        </span>
                    </Field>
                </div>
                <div className="mt-2 flex flex-col gap-1 text-[12px]"
                     data-testid={`ws5-apt-executed-doc-list-${apt.id}`}>
                    {executedDocs.map((ed, idx) => (
                        <div key={ed.file_id || idx}
                             className="flex items-center flex-wrap gap-2"
                             data-testid={`ws5-apt-executed-doc-row-${apt.id}-${idx}`}>
                            <span className="font-mono text-[11.5px]">
                                {ed.filename || ed.file_id?.slice(0, 8)}
                            </span>
                            <span className="text-[10.5px] text-[#666]">
                                v{ed.current_version_number}
                            </span>
                            <a href={`${API}/admin/files/${ed.file_id}/preview`}
                                data-testid={`ws5-apt-view-executed-${apt.id}-${idx}`}
                                className="text-[#A8543F] underline">
                                View
                            </a>
                            <a href={`${API}/admin/files/${ed.file_id}/download`}
                                data-testid={`ws5-apt-download-executed-${apt.id}-${idx}`}
                                className="text-[#A8543F] underline">
                                Download
                            </a>
                        </div>
                    ))}
                </div>
                {toast && (
                    <div data-testid={`ws5-apt-toast-${apt.id}`}
                         className="mt-3 text-[12px] text-green-800 bg-green-50 border border-green-200 rounded p-2">
                        {toast}
                    </div>
                )}
                {/* Phase 3 — lifecycle action buttons */}
                <div className="mt-3 flex flex-wrap gap-2">
                    {showExecUploadBtn && (
                        <>
                            <button type="button"
                                    disabled
                                    aria-disabled="true"
                                    title="Coming soon — in-app creation with e-signature. Use 'Upload Signed Appointment' for externally-signed documents."
                                    data-testid={`ws5-apt-create-disabled-${apt.id}`}
                                    className="text-[11px] px-3 py-1 rounded border border-[#E5E2DC] text-[#999] bg-[#F5F3EE] cursor-not-allowed">
                                Create Appointment
                            </button>
                            <button type="button"
                                    onClick={() => setShowExecUpload(true)}
                                    data-testid={`ws5-apt-exec-upload-btn-${apt.id}`}
                                    className="text-[11px] px-3 py-1 rounded bg-[#A8543F] text-white">
                                Upload Signed Appointment
                            </button>
                        </>
                    )}
                    {showPartialBtn && (
                        <button type="button"
                                onClick={() => openAction("partial")}
                                data-testid={`ws5-apt-partial-btn-${apt.id}`}
                                className="text-[11px] px-3 py-1 rounded bg-[#F5F3EE]">
                            Mark Partially Signed
                        </button>
                    )}
                    {showWithdrawBtn && (
                        <button type="button"
                                onClick={() => openAction("withdraw")}
                                data-testid={`ws5-apt-withdraw-btn-${apt.id}`}
                                className="text-[11px] px-3 py-1 rounded bg-[#F5F3EE]">
                            Withdraw
                        </button>
                    )}
                    {showFlagBtn && (
                        <button type="button"
                                onClick={() => openAction("flag")}
                                data-testid={`ws5-apt-flag-btn-${apt.id}`}
                                className="text-[11px] px-3 py-1 rounded bg-[#F5F3EE]">
                            Flag Signature Issue
                        </button>
                    )}
                    {showAddendumBtns && (
                        <>
                            <button type="button"
                                    disabled
                                    aria-disabled="true"
                                    title="Coming soon — in-app creation with e-signature. Use 'Upload Signed Addendum' for externally-signed documents."
                                    data-testid={`ws5-apt-create-addendum-disabled-${apt.id}`}
                                    className="text-[11px] px-3 py-1 rounded border border-[#E5E2DC] text-[#999] bg-[#F5F3EE] cursor-not-allowed">
                                Create Addendum
                            </button>
                            <button type="button"
                                    onClick={() => setShowUploadAddendum(true)}
                                    data-testid={`ws5-apt-upload-addendum-btn-${apt.id}`}
                                    className="text-[11px] px-3 py-1 rounded bg-[#A8543F] text-white">
                                Upload Signed Addendum
                            </button>
                        </>
                    )}
                </div>
                {/* Phase 3 — enriched executed-document block (post-execution) */}
                {executedDocs.length > 0 && (execEv.method_summary || extSigners.length > 0) && (
                    <div className="mt-3 text-[12px] bg-[#FCFBF9] border border-[#E5E2DC] rounded p-3"
                         data-testid={`ws5-apt-ext-exec-${apt.id}`}>
                        {execEv.method_summary && (
                            <div data-testid={`ws5-apt-ext-exec-method-${apt.id}`}>
                                <strong>Method:</strong> {execEv.method_summary}
                            </div>
                        )}
                        {execEv.external_ref && (
                            <div data-testid={`ws5-apt-ext-exec-ref-${apt.id}`}>
                                <strong>External ref:</strong> {execEv.external_ref}
                            </div>
                        )}
                        {extSigners.length > 0 && (
                            <div data-testid={`ws5-apt-ext-exec-signers-${apt.id}`} className="mt-1">
                                <strong>External signers:</strong>
                                <ul className="list-disc ml-5 text-[11px]">
                                    {extSigners.map(s => (
                                        <li key={s.id} data-testid={`ws5-apt-ext-exec-signer-${apt.id}-${s.id}`}>
                                            {s.signer_name} — {s.signer_role}
                                            {" · signed "}{s.signed_on}
                                            {" · "}
                                            <span className="inline-flex rounded-full bg-[#F5F3EE] px-2 py-0.5 text-[10px]">
                                                {EXT_METHOD_LABELS[s.signature_method] || s.signature_method}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* Panel 4 — Package Activation Conditions */}
            <section data-testid={`ws5-apt-activation-${apt.id}`}>
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Package Activation Conditions
                </h4>
                {includedPkgs.length === 0
                    ? <div className="text-[12px] text-[#666]"
                              data-testid={`ws5-apt-activation-empty-${apt.id}`}>
                        No packages included in this appointment.
                    </div>
                    : (
                        <div className="flex flex-col gap-2">
                            {includedPkgs.map((p) => {
                                const rd = p.activation_readiness || {};
                                const reasons = rd.blocker_reasons
                                    || rd.reasons || [];
                                return (
                                    <div key={p.id}
                                        className="rounded border border-[#E5E2DC] bg-white p-2"
                                        data-testid={`ws5-apt-pkg-cond-${apt.id}-${p.id}`}>
                                        <div className="flex items-center gap-2">
                                            <strong className="text-[13px]">
                                                {p.package_name}
                                            </strong>
                                            <Pill
                                                tone={rd.ready ? "good" : "warn"}
                                                testid={`ws5-apt-pkg-cond-status-${apt.id}-${p.id}`}>
                                                {rd.ready ? "Ready" : "Blocked"}
                                            </Pill>
                                            <span className="text-[11px] text-[#666] ml-2"
                                                    data-testid={`ws5-apt-pkg-cond-authority-${apt.id}-${p.id}`}>
                                                Authority: {apt.status === "Executed"
                                                    ? "Executed Appointment" : "Appointment pending execution"}
                                            </span>
                                            {rd.ready && p.status === "Appointed" && (
                                                <button type="button"
                                                    onClick={() => {
                                                        setConfirmNotify(false);
                                                        setConfirmActivate(p);
                                                    }}
                                                    data-testid={`ws5-apt-pkg-activate-${apt.id}-${p.id}`}
                                                    className="ml-auto text-[12px] rounded bg-[#345E34] text-white px-2 py-0.5">
                                                    Activate Package
                                                </button>)}
                                        </div>
                                        {reasons.length > 0 && (
                                            <ul className="mt-1 list-disc pl-5 text-[11px] text-[#7A5148]"
                                                data-testid={`ws5-apt-pkg-cond-blockers-${apt.id}-${p.id}`}>
                                                {reasons.map((r, i) => (
                                                    <li key={i}>{
                                                        typeof r === "string"
                                                            ? r
                                                            : (r.reason || JSON.stringify(r))
                                                    }</li>))}
                                            </ul>)}
                                        <div className="mt-1 grid grid-cols-2 gap-x-4 text-[11px] text-[#666]">
                                            <div data-testid={`ws5-apt-pkg-cond-prereq-${apt.id}-${p.id}`}>
                                                Prerequisites: {(p.snapshot?.prerequisites?.length
                                                    || p.project_configuration?.prerequisites?.length
                                                    || 0)}
                                            </div>
                                            <div data-testid={`ws5-apt-pkg-cond-client-${apt.id}-${p.id}`}>
                                                Client dependencies: {(p.snapshot?.client_dependencies?.length
                                                    || 0)}
                                            </div>
                                            <div data-testid={`ws5-apt-pkg-cond-payment-${apt.id}-${p.id}`}>
                                                Payment condition: {p.snapshot?.payment_condition
                                                    || p.project_configuration?.payment_condition
                                                    || "—"}
                                            </div>
                                            <div data-testid={`ws5-apt-pkg-cond-activation-${apt.id}-${p.id}`}>
                                                Activation conditions: {(p.snapshot?.activation_conditions?.length
                                                    || p.project_configuration?.activation_conditions?.length
                                                    || 0)}
                                            </div>
                                        </div>
                                    </div>);
                            })}
                        </div>
                    )}
            </section>

            {/* Panel 5 — Current Appointed Scope */}
            <section data-testid={`ws5-apt-scope-${apt.id}`}>
                <h4 className="font-manrope text-[12px] uppercase text-[#666] tracking-wide mb-2">
                    Current Appointed Scope
                </h4>
                {includedPkgs.length === 0
                    ? <div className="text-[12px] text-[#666]"
                              data-testid={`ws5-apt-scope-empty-${apt.id}`}>
                        No appointed packages yet.
                    </div>
                    : (
                        <ul className="list-disc pl-5 text-[12px]"
                              data-testid={`ws5-apt-scope-list-${apt.id}`}>
                            {includedPkgs.map((p) => (
                                <li key={p.id}
                                    data-testid={`ws5-apt-scope-item-${apt.id}-${p.id}`}>
                                    <strong>{p.package_name}</strong>
                                    {" — "}status: {p.status}
                                    {p.project_configuration?.project_specific_scope && (
                                        <div className="text-[#666] mt-0.5">
                                            {p.project_configuration.project_specific_scope}
                                        </div>)}
                                </li>))}
                        </ul>
                    )}
                <div className="mt-2 text-[11px] text-[#666]">
                    Read from authoritative Appointment / Contract Record /
                    project Service Packages. Addenda / Variations are
                    handled in a later gate.
                </div>
            </section>
            {confirmActivate && (
                <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center"
                    data-testid={`ws5-activate-confirm-${confirmActivate.id}`}>
                    <div className="bg-white rounded shadow-lg w-[min(480px,92vw)] p-4">
                        <h3 className="font-manrope text-[14px] font-semibold text-charcoal">
                            Activate package
                        </h3>
                        <p className="text-[12px] text-[#666] mt-1">
                            Activate <strong>{confirmActivate.package_name}</strong>?
                            This instantiates operational content and cannot be undone from this page.
                        </p>
                        <label className="text-[12px] flex items-center gap-2 mt-3">
                            <input type="checkbox" checked={confirmNotify}
                                onChange={(e) => setConfirmNotify(e.target.checked)}
                                data-testid={`ws5-activate-notify-client-${confirmActivate.id}`} />
                            Send client notification
                        </label>
                        <div className="mt-4 flex justify-end gap-2">
                            <button type="button"
                                onClick={() => { setConfirmActivate(null); setConfirmNotify(false); }}
                                className="text-[12px] px-3 py-1.5"
                                data-testid={`ws5-activate-cancel-${confirmActivate.id}`}>Cancel</button>
                            <button type="button" onClick={doActivate}
                                disabled={confirmBusy}
                                className="text-[12px] px-3 py-1.5 rounded bg-[#345E34] text-white"
                                data-testid={`ws5-activate-confirm-btn-${confirmActivate.id}`}>
                                {confirmBusy ? "Activating…" : "Activate"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <RecordExternalExecutionModal
                open={showExecUpload}
                onClose={() => setShowExecUpload(false)}
                endpointUrl={`${API}/admin/projects/${projectId}/ws5/appointments/${apt.id}/execute-with-upload`}
                submitLabel="Execute Appointment"
                helpText={"Attach the externally-signed appointment PDF and capture structured "
                         + "signer evidence.  The appointment will be executed AND the linked "
                         + "contract marked effective in a single step."}
                testIdPrefix={`ws5-apt-exec-upload-${apt.id}`}
                onSuccessData={() => {
                    setToast("Appointment executed and contract marked effective.");
                    onChanged && onChanged();
                }} />
            <UploadSignedAddendumModal
                open={showUploadAddendum}
                onClose={() => setShowUploadAddendum(false)}
                projectId={projectId}
                appointmentId={apt.id}
                onSuccess={() => {
                    setToast("Appointment addendum recorded.");
                    onChanged && onChanged();
                }} />
            <_AppointmentActionPrompt
                open={actionKind === "partial"}
                title="Mark Partially Signed"
                label="Optional note"
                required={false}
                submitLabel="Mark Partially Signed"
                busy={actionBusy} err={actionErr}
                value={actionText} onChange={setActionText}
                onCancel={closeAction} onSubmit={submitAction}
                testIdPrefix={`ws5-apt-partial-${apt.id}`} />
            <_AppointmentActionPrompt
                open={actionKind === "withdraw"}
                title="Withdraw Appointment"
                label="Reason for withdrawal"
                required submitLabel="Withdraw"
                busy={actionBusy} err={actionErr}
                value={actionText} onChange={setActionText}
                onCancel={closeAction} onSubmit={submitAction}
                testIdPrefix={`ws5-apt-withdraw-${apt.id}`} />
            <_AppointmentActionPrompt
                open={actionKind === "flag"}
                title="Flag Signature Issue"
                label="Issue description"
                required submitLabel="Flag Issue"
                busy={actionBusy} err={actionErr}
                value={actionText} onChange={setActionText}
                onCancel={closeAction} onSubmit={submitAction}
                testIdPrefix={`ws5-apt-flag-${apt.id}`} />
        </div>);
}


export default WS5ActiveModules;
